/**
 * Saved-visuals UI for the in-app preview Assistant (builder).
 *
 * `VisualFullscreen` — an overlay that renders one visual large (used by the
 * chat's "Open" button and the browser below).
 * `SavedVisualsPanel` — the "My saved visuals" browser shown in the launcher
 * panel: list the user's PRIVATE saved visuals, open / delete them, and (for
 * the owner, `canStandardize`) STANDARDISE one into a first-class Visual (V)
 * object usable in pages and navigation.
 *
 * Runtime twin: `runtime/client/src/assistant/SavedVisuals.tsx` (no
 * standardise — that is an application-design action).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { InsightVisual } from '@twasta/visual-engine'
import type { VisualJsonData, VisualLayout, VisualOption } from '@twasta/visual-engine'
import {
  assistantSavedVisuals, assistantSavedVisualData, assistantDeleteSavedVisual,
  assistantStandardizeVisual, type SavedVisualMeta, type SavedVisualData,
} from '@/api/assistant'

export function VisualFullscreen({
  title, jsonData, visualLayout, optionsArr, chartType, onClose,
}: {
  title?: string
  jsonData: VisualJsonData
  visualLayout: VisualLayout
  optionsArr: VisualOption[]
  chartType?: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  // Portal to <body>: the launcher's docked panel is `position:fixed; z-index
  // 1200`, which establishes a stacking context. Rendered inside it, this
  // overlay's z-index 9800 is trapped at the panel's 1200 level, so the shell's
  // sticky top bar (z 9000) paints over its top edge. Portaling escapes that
  // context so 9800 competes at the page root and clears the top bar.
  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={sheetHdr}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title || 'Visual'}</span>
          <button onClick={onClose} style={xBtn} aria-label="Close" title="Close (Esc)">✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: 14, overflow: 'auto' }}>
          <InsightVisual jsonData={jsonData} visualLayout={visualLayout} optionsArr={optionsArr}
            chartType={chartType} style={{ width: '100%', height: '100%' }} />
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function SavedVisualsPanel({
  project, canStandardize = false, onBack,
}: { project: string; canStandardize?: boolean; onBack?: () => void }) {
  const [items, setItems] = useState<SavedVisualMeta[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<SavedVisualData | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = () => {
    setError(null)
    assistantSavedVisuals(project)
      .then(setItems)
      .catch(() => { setItems([]); setError('Could not load saved visuals.') })
  }
  useEffect(load, [project])

  const openVisual = async (m: SavedVisualMeta) => {
    setBusyId(m.id); setError(null)
    try { setOpen(await assistantSavedVisualData(project, m.id)) }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not open the visual.') }
    finally { setBusyId(null) }
  }
  const remove = async (m: SavedVisualMeta) => {
    if (!window.confirm(`Delete “${m.title}”?`)) return
    await assistantDeleteSavedVisual(project, m.id).catch(() => {})
    load()
  }
  const standardize = async (m: SavedVisualMeta) => {
    const name = window.prompt('Standardise as a visual named:', m.title)?.trim()
    if (!name) return
    setBusyId(m.id); setError(null)
    try { await assistantStandardizeVisual(project, m.id, name); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not standardise.') }
    finally { setBusyId(null) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#fff', color: '#1f2937' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #e5e7eb' }}>
        {onBack && <button onClick={onBack} style={linkBtn}>← Chat</button>}
        <span style={{ fontWeight: 700, fontSize: 15 }}>My saved visuals</span>
        <button onClick={load} style={{ ...linkBtn, marginLeft: 'auto' }}>Refresh</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {error && <div style={{ fontSize: 14, color: '#dc2626' }}>{error}</div>}
        {items === null && <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>}
        {items !== null && items.length === 0 &&
          <div style={{ fontSize: 14, color: '#6b7280' }}>No saved visuals yet. Ask for a chart, then click Save on it.</div>}
        {(items || []).map((m) => (
          <div key={m.id} style={card}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                {[m.chart_type, m.business_area].filter(Boolean).join(' · ')}{m.standardized_as ? ' · standardised' : ''}
              </div>
            </div>
            <button onClick={() => openVisual(m)} disabled={busyId === m.id} style={smBtn}>{busyId === m.id ? '…' : 'Open'}</button>
            {canStandardize && (
              <button onClick={() => standardize(m)} disabled={busyId === m.id} style={smBtn}
                title="Copy into the project's standard visuals so it can be used in pages and navigation">Standardise</button>
            )}
            <button onClick={() => remove(m)} style={{ ...smBtn, color: '#dc2626' }} title="Delete">✕</button>
          </div>
        ))}
      </div>
      {open && (
        <VisualFullscreen
          title={open.title}
          jsonData={open.jsonData as VisualJsonData}
          visualLayout={open.visualLayout as VisualLayout}
          optionsArr={(open.optionsArr as VisualOption[]) || []}
          chartType={open.chartType}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9800, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
}
const sheet: React.CSSProperties = {
  width: 'min(1100px, 96vw)', height: 'min(760px, 92vh)', background: '#fff', color: '#1f2937',
  borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
}
const sheetHdr: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 14px', borderBottom: '1px solid #e5e7eb',
}
const xBtn: React.CSSProperties = { border: 'none', background: 'transparent', fontSize: 19, cursor: 'pointer', color: '#6b7280' }
const linkBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: '#4f46e5', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: 2 }
const smBtn: React.CSSProperties = { border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer', color: '#374151' }
const card: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 10px', background: '#fff' }
